import { useMemo } from 'react';
import { useRouter } from 'next/router';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useFiltersForSponsoredResources } from 'src/dispensary/hooks/use-filters-for-sponsored-resources';
import { parseProduct, Product } from 'src/utils/helpers/product';
import { SponsoredProductsInventoryId, useGetSponsoredProductsQuery } from 'types/graphql';
import { ADS_FETCH_POLICY } from 'src/utils/ads/helpers';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import type { ApolloError } from 'apollo-client';

import { useSponsoredProductsFilter } from './use-decorate-with-sponsored-products';

type UseGetOnlySponsoredProductsReturn = {
  products: Product[];
  loading: boolean;
  error: ApolloError | undefined;
};

export function useGetOnlySponsoredProducts(): UseGetOnlySponsoredProductsReturn {
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id;
  const { filters } = useFiltersForSponsoredResources();

  const { data, loading, error } = useGetSponsoredProductsQuery({
    fetchPolicy: ADS_FETCH_POLICY,
    variables: {
      input: {
        limit: 20,
        dispensaryId,
        inventoryId: SponsoredProductsInventoryId.cartTopperProductAuc,
        filters,
      },
    },
  });

  const unfilteredSponsoredProductsData: Product[] = data?.getSponsoredProducts as Product[];
  const { query } = useRouter();
  const { queryParams: qp, defaults } = useProductQueryParams();

  // Remove sponsored products which do not match the current menu filters
  const sponsoredProductsData = useSponsoredProductsFilter(
    unfilteredSponsoredProductsData,
    query,
    qp,
    defaults,
    '',
    dispensaryId
  );

  const serializedSponsoredProducts = useMemo(() => {
    if (loading || error) {
      return [];
    }

    return sponsoredProductsData.map(parseProduct);
  }, [error, loading, sponsoredProductsData]);

  return {
    products: serializedSponsoredProducts,
    loading,
    error,
  };
}
